<template>
  <div class="upload-modal__content-wrapper">
    <div
      class="upload-modal__image-wrapper"
      :class="{ 'upload-modal__image-wrapper--no-padding': cropper }"
    >
      <template v-if="isImg">
        <div v-if="isShowCropped" class="upload-modal__preview-wrapper">
          <img :src="croppedImageSrc" alt="image" class="upload-modal__preview-image">
        </div>
        <img
          ref="image"
          :src="imageSrc"
          alt="image"
          crossorigin="anonymous"
        >
        <template v-if="isRotate">
          <v-btn
            icon
            class="upload-modal__rotate-button upload-modal__rotate-button--left"
            @click="rotate(true)"
          >
            <v-icon>mdi-rotate-left</v-icon>
          </v-btn>
          <v-btn
            icon
            class="upload-modal__rotate-button upload-modal__rotate-button--right"
            @click="rotate()"
          >
            <v-icon>mdi-rotate-right</v-icon>
          </v-btn>
        </template>
      </template>
      <div v-else class="upload-modal__files-wrapper">
        <v-card variant="outlined" data-test="upload-files-card">
          <v-card-title class="d-flex align-center py-2 text-subtitle-1 font-weight-medium">
            Files
            <span class="text-medium-emphasis ms-1" data-test="upload-files-count">({{ uploadedFiles.length }})</span>
            <v-spacer />
            <span class="text-body-2 text-medium-emphasis" data-test="upload-files-total">
              {{ formatBytes(totalSize) }} total
            </span>
          </v-card-title>
          <v-divider />
          <v-list class="upload-modal__files-list py-0 bg-transparent">
            <template v-for="(file, index) in uploadedFiles" :key="`${file.name}-${file.size}-${file.lastModified}`">
              <v-divider v-if="index" />
              <v-list-item data-test="upload-file-row">
                <template #prepend>
                  <v-avatar rounded size="40" class="me-3 upload-modal__file-thumb">
                    <v-img
                      v-if="thumbnails[index]"
                      :src="thumbnails[index]"
                      cover
                      alt=""
                      data-test="upload-file-thumb"
                    />
                    <v-icon v-else data-test="upload-file-icon">{{ fileIcon(file) }}</v-icon>
                  </v-avatar>
                </template>
                <v-list-item-title :title="file.name">{{ file.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ formatBytes(file.size) }}</v-list-item-subtitle>
                <template #append>
                  <v-tooltip text="Remove file" location="top">
                    <template #activator="{ props: tooltipProps }">
                      <v-btn
                        v-bind="tooltipProps"
                        icon="mdi-close"
                        size="small"
                        variant="text"
                        aria-label="Remove file"
                        :disabled="isLoading"
                        data-test="upload-file-remove"
                        @click="$emit('removeFile', index)"
                      />
                    </template>
                  </v-tooltip>
                </template>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </div>
      <div v-if="isLoading" class="upload-modal__progress-overlay">
        <div class="upload-modal__progress-box">
          <div class="mb-2">{{ uploadStatus }}</div>
          <v-progress-linear
            :model-value="uploadProgress ?? 0"
            :indeterminate="uploadProgress === null || isServerProcessing"
            height="6"
            rounded
          />
          <v-btn
            v-if="uploadProgress !== null && !isServerProcessing"
            variant="outlined"
            class="mt-4"
            @click="cancelUpload"
          >
            Cancel
          </v-btn>
        </div>
      </div>
    </div>
    <v-card-actions class="upload-modal__actions">
      <v-btn
        v-if="isImg && cropper"
        :disabled="isLoading"
        variant="outlined"
        @click="checkCropped"
      >
        {{ isShowCropped ? 'Hide' : 'Show' }} Preview
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-upload"
        :loading="isLoading"
        data-test="upload-submit"
        @click="submit"
      >
        {{ submitLabel }}
      </v-btn>
    </v-card-actions>
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent, markRaw } from 'vue';
import Cropper from 'cropperjs';

import { CropperFuncNamesEnum, formatBytes } from '@/bundles/Upload/helpers';
import { Nullable } from '@/utils/types';
import { IUploadOptions } from '@/bundles/Upload/interfaces';
import { uploadService } from '@/bundles/Upload/factory/UploadServiceFactory';
import { useNotifications } from '@/bundles/App/composables/useNotifications';
import { getAttachmentIcon } from '@/bundles/Attachment/helpers/getAttachmentIcon';

interface IUploadCropper {
  cropper: Nullable<Cropper>;
  isRotate: boolean;
  isShowCropped: boolean;
  isLoading: boolean;
  croppedImageSrc: string;
  uploadProgress: Nullable<number>;
  uploadController: Nullable<AbortController>;
  // object urls for the image files in the list, by index; '' for other files
  thumbnails: string[];
}

const FILL_COLOR = '#FFFFFF';

export default defineComponent({
  name: 'UploadCropper',

  props: {
    imageSrc: {
      type: String,
      default: null
    },
    uploadedFiles: {
      type: Array as PropType<File[]>,
      default: () => []
    },
    activeFunc: {
      type: String as PropType<CropperFuncNamesEnum>,
      default: null
    },
    options: {
      type: Object as PropType<IUploadOptions>,
      required: true,
    },
    isImg: {
      type: Boolean,
      required: true,
    },
    sourceType: {
      type: String,
      default: null,
    },
    sourceId: {
      type: [String, Number],
      default: null,
    },
    isMultiplySizesUpload: Boolean,
    attachmentType: {
      type: String,
      default: null,
    },
    isReturnFileWithoutUpload: Boolean,
  },

  emits: ['fileUploaded', 'removeFile', 'update:uploading'],

  setup () {
    const { addNotification } = useNotifications();

    return { addNotification };
  },

  data: (): IUploadCropper => ({
    cropper: null,
    isRotate: false,
    isShowCropped: false,
    isLoading: false,
    croppedImageSrc: '',
    uploadProgress: null,
    uploadController: null,
    thumbnails: [],
  }),

  computed: {
    totalSize (): number {
      return this.uploadedFiles.reduce((total, file) => total + file.size, 0);
    },
    submitLabel (): string {
      return this.uploadedFiles.length > 1 ? `Upload ${this.uploadedFiles.length} files` : 'Upload';
    },
    isServerProcessing (): boolean {
      return this.uploadProgress === 100;
    },
    uploadStatus (): string {
      if (this.uploadProgress === null) {
        return 'Preparing image...';
      }

      return this.isServerProcessing ? 'Processing on server...' : `Uploading ${this.uploadProgress}%`;
    },
  },

  watch: {
    isLoading (value: boolean) {
      this.$emit('update:uploading', value);
    },
    activeFunc: {
      handler (newName: Nullable<CropperFuncNamesEnum>, prevName: undefined | CropperFuncNamesEnum) {
        if (newName) {
          this.toggleCropperFunc(newName);
        } else if (prevName && !newName) {
          this.cropper?.destroy();
          this.cropper = null;
          this.isRotate = false;
        }
      },
      immediate: true,
    },
    // a new list (a file removed or another batch picked) gets fresh previews; the old urls are released.
    // deep: Upload removes a file with splice, which a plain watcher on the array never sees (Vue 3).
    uploadedFiles: {
      handler (files: File[]) {
        this.revokeThumbnails();
        this.thumbnails = files.map(file => (file.type.startsWith('image/') ? URL.createObjectURL(file) : ''));
      },
      deep: true,
      immediate: true,
    },
  },

  mounted () {
    this.$eventBus.$on('upload:zoom', this.zoomChangeHandler);
  },

  beforeUnmount () {
    this.cropper?.destroy();
    this.revokeThumbnails();
    this.$eventBus.$off('upload:zoom', this.zoomChangeHandler);
  },

  methods: {
    revokeThumbnails () {
      this.thumbnails.forEach(url => url && URL.revokeObjectURL(url));
    },
    fileIcon (file: File): string {
      return getAttachmentIcon({ original: { name: file.name }, url: '' } as Parameters<typeof getAttachmentIcon>[0]);
    },
    initCropper () {
      if (this.cropper) {
        return;
      }

      this.$nextTick(() => {
        this.cropper = markRaw(new Cropper((this.$refs.image as HTMLImageElement), {
          background: false,
          viewMode: 0,
          autoCropArea: 1,
          checkCrossOrigin: false,
          ...this.options
        }));
      });
    },
    submit () {
      if (!this.uploadedFiles.length) {
        return;
      }

      this.isLoading = true;

      if (this.cropper) {
        const fileType = this.uploadedFiles[0].type || 'image/png';

        const imageData = this.cropper.getData();

        this.cropper.getCroppedCanvas({
          width: Math.round(imageData.width),
          height: Math.round(imageData.height),
        }).toBlob((blob) => {
          if (!blob) {
            return;
          }

          const file = new File(
            [blob],
            this.uploadedFiles[0].name || 'cropped.png', // cropper load only when one file present
            { type: fileType } // file can be null if we upload image from url
          );

          if (this.isReturnFileWithoutUpload) {
            this.$emit('fileUploaded', { originalFiles: [], files: [], file });
            this.isLoading = false;
            return;
          }

          this.saveFile({ files: [file] });
        }, fileType, 0.95);
        return;
      }

      if (this.uploadedFiles.length) {
        this.saveFile({ files: this.uploadedFiles });
      }
    },
    async saveFile ({ files }: { files: File[] }) {
      try {
        this.isLoading = true;
        this.uploadProgress = 0;
        this.uploadController = new AbortController();

        const params: Array<{ name: string; value: any }> = [];

        if (this.attachmentType) {
          params.push(
            { name: 'is_attachment', value: true },
            { name: 'attachment_type', value: this.attachmentType }
          );
        }

        // bind is used to save context of this in uploadService
        const uploadFunction = this.isMultiplySizesUpload ? uploadService.uploadMultiplySizes.bind(uploadService) : uploadService.upload.bind(uploadService);
        const responseFiles = await uploadFunction({
          record: this.sourceType,
          key: this.sourceId,
          files,
          params,
          onUploadProgress: this.onUploadProgress,
          controller: this.uploadController,
        });

        this.$emit('fileUploaded', { originalFiles: files, files: responseFiles });
      } catch (error) {
        if (this.uploadController?.signal.aborted) {
          return;
        }

        const notification = { ...error, status: 0 }; // to show error notification
        this.addNotification(notification);
      } finally {
        this.isLoading = false;
        this.uploadProgress = null;
        this.uploadController = null;
      }
    },
    cancelUpload () {
      this.uploadController?.abort();
    },
    onUploadProgress ({ loaded, total }: ProgressEvent) {
      if (total) {
        this.uploadProgress = Math.round((loaded / total) * 100);
      }
    },
    rotate (isLeft = false) {
      this.cropper?.rotate(isLeft ? -90 : 90);
    },
    toggleCropperFunc (name: CropperFuncNamesEnum) {
      if (!this.isImg) {
        return;
      }

      if (!this.cropper) {
        this.initCropper();
      }

      this.isRotate = name === CropperFuncNamesEnum.rotate;
    },
    hideCroppedImage () {
      this.croppedImageSrc = '';
      this.isShowCropped = false;
    },
    checkCropped () {
      if (this.croppedImageSrc) {
        this.hideCroppedImage();
        return;
      }

      if (!this.cropper) {
        return;
      }

      const imageData = this.cropper.getData();

      this.cropper.getCroppedCanvas({
        width: Math.round(imageData.width),
        height: Math.round(imageData.height),
        fillColor: FILL_COLOR
      }).toBlob((blob) => {
        this.croppedImageSrc = URL.createObjectURL(blob);
        this.isShowCropped = true;
      });
    },
    formatBytes (bytes: number): string {
      return formatBytes(bytes);
    },
    zoomChangeHandler (isZoomIn = false) {
      if (!this.cropper) {
        return;
      }

      this.cropper.zoom(isZoomIn ? 0.1 : -0.1);
    }
  }
});
</script>

<style lang="scss">
@import "~cropperjs/dist/cropper.min.css";

.upload-modal__content-wrapper {
  width: 100%;
  height: 100%;
  // the image takes what the button bar leaves, whatever the bar's height
  display: flex;
  flex-direction: column;

  .upload-modal__image-wrapper {
    position: relative;
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: padding ease 0.3s;
  }

  .upload-modal__image-wrapper--no-padding {
    padding: 0;
  }

  img {
    display: block;
    max-width: 100%;
    max-height: 100%;
  }
}

.upload-modal__progress-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  // the modal's own surface, so the overlay follows the theme instead of washing a dark modal out in white
  background-color: rgba(var(--v-theme-sc-upload-surface), 0.85);
}

.upload-modal__progress-box {
  width: 60%;
  text-align: center;
}

.upload-modal__rotate-button {
  position: absolute;
  top: 50%;
  background-color: #ffffff;
  opacity: 0.75;
  transform: translateY(-50%);
}

.upload-modal__rotate-button--left {
  left: 20px;
}

.upload-modal__rotate-button--right {
  right: 20px;
}

.upload-modal__actions {
  flex: 0 0 auto;
  padding: 8px 16px !important;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.upload-modal__files-wrapper {
  width: 100%;
  padding: 16px;
}

// the list scrolls under its header once there are more files than fit
.upload-modal__files-list {
  max-height: 320px;
  overflow-y: auto;
}

.upload-modal__file-thumb {
  background-color: rgba(var(--v-theme-on-surface), 0.08);
}

.upload-modal__preview-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgb(var(--v-theme-sc-upload-surface));
  z-index: 10;
  width: 100%;
  height: 100%;
  padding: 8px;
  transition: all ease 0.3s;

  .upload-modal__preview-image {
    border: 1px solid rgb(var(--v-theme-sc-upload-surface));
    border-radius: 4px;
    margin: auto;
  }
}
</style>
